import { CheckCircle2, ClipboardCheck, DollarSign, Handshake, Shield, Target, Users, Wrench, type LucideIcon } from "lucide-react"
import { GoNoGoCriterionCard } from "@/components/strategy/GoNoGoCriterionCard"
import { categoryAverage, categoryAvgTone, displayCategoryLabel, TONE_PILL_CLASS } from "@/lib/gng-score"
import { cn } from "@/lib/utils"
import type { GoNoGoCriterion, AiScoringSource } from "@/stores/strategy-store"

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  strategic: Target,
  capability: Wrench,
  commercial: DollarSign,
  risk: Shield,
  resource: Users,
  relationship: Handshake,
}

interface GoNoGoCategoryGroupProps {
  categoryLabel: string
  categorySlug?: string | undefined
  criteria: GoNoGoCriterion[]
  onScoreChange: (criterionId: string, score: number | null) => void
  onEvidenceChange: (criterionId: string, evidence: string) => void
  onWeightChange?: ((criterionId: string, weight: number) => void) | undefined
  onRemove?: ((criterionId: string) => void) | undefined
  onAiScoreCriterion?: ((criterionId: string) => void) | undefined
  readOnly?: boolean
  canEditStructure?: boolean
  scoringSources?: AiScoringSource[] | null | undefined
}

export function GoNoGoCategoryGroup({
  categoryLabel,
  categorySlug,
  criteria,
  onScoreChange,
  onEvidenceChange,
  onWeightChange,
  onRemove,
  onAiScoreCriterion,
  readOnly = false,
  canEditStructure = false,
  scoringSources,
}: GoNoGoCategoryGroupProps) {
  const scored = criteria.filter(c => c.score != null).length
  const total = criteria.length
  const allScored = scored === total && total > 0
  const avg = categoryAverage(criteria)
  const Icon = CATEGORY_ICONS[categorySlug ?? ''] ?? ClipboardCheck

  return (
    <div>
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">
            {displayCategoryLabel(categoryLabel)}
          </span>
          {allScored && (
            <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--status-success))]" />
          )}
        </div>
        <div className="flex items-center gap-2.5">
          {avg != null && (
            <span className={cn("rounded-full px-2 py-0.5 text-[11px] tabular-nums", TONE_PILL_CLASS[categoryAvgTone(avg)])}>
              {avg.toFixed(1)} avg
            </span>
          )}
          <span className="text-meta">{scored} of {total} scored</span>
        </div>
      </div>

      <div className="border border-border rounded-xl bg-card shadow-sm divide-y divide-border">
        {criteria.map(criterion => (
          <GoNoGoCriterionCard
            key={criterion.id}
            criterion={criterion}
            onScoreChange={(score) => onScoreChange(criterion.id, score)}
            onEvidenceChange={(evidence) => onEvidenceChange(criterion.id, evidence)}
            onWeightChange={onWeightChange ? (weight) => onWeightChange(criterion.id, weight) : undefined}
            onRemove={onRemove ? () => onRemove(criterion.id) : undefined}
            onAiScore={onAiScoreCriterion ? () => onAiScoreCriterion(criterion.id) : undefined}
            readOnly={readOnly}
            canEditStructure={canEditStructure}
            scoringSources={scoringSources}
          />
        ))}
      </div>
    </div>
  )
}
